'use client';
import * as React from 'react';
import { useTranslations } from 'next-intl';
import { ArrowLeft, ArrowRight, BookOpenCheck, FileDown, Heart, Images, Layers, Sparkles } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useRouter } from '@/i18n/navigation';
import { Button, Card } from '@freealbum/ui';
import type { AlbumSummary } from '@freealbum/shared';
import { SignInForm } from '@/components/auth/sign-in';
import { Logo } from '@/components/layout/logo';
import { useMe } from '@/hooks/use-me';
import { RecentAlbums } from './recent-albums';
import { cn } from '@/lib/utils';

/** Decorative stack of "pages" behind the hero — a book, not a dashboard. */
function HeroArt() {
  return (
    <div aria-hidden className="pointer-events-none relative mx-auto hidden aspect-4/3 w-full max-w-md md:block">
      <div className="absolute inset-x-6 top-6 h-full rotate-[-4deg] rounded-lg border border-line bg-surface-2 shadow-md" />
      <div className="absolute inset-x-3 top-3 h-full rotate-[2deg] rounded-lg border border-line bg-surface-2 shadow-md" />
      <div className="absolute inset-0 grid grid-cols-2 overflow-hidden rounded-lg border border-line bg-surface-2 shadow-lg">
        <div className="grid grid-rows-3 gap-2 border-e border-line p-3">
          <div className="row-span-2 rounded bg-terracotta-100" />
          <div className="rounded bg-saffron-100" />
        </div>
        <div className="flex flex-col gap-2 p-3">
          <div className="flex-1 rounded bg-moss-100" />
          <div className="k-nikud text-center font-display text-lg text-ink" dir="rtl" lang="he">
            קַיִץ בַּגָּלִיל
          </div>
          <div className="h-2 rounded-full bg-line" />
          <div className="h-2 w-2/3 rounded-full bg-line" />
        </div>
      </div>
    </div>
  );
}

const VALUE_PROPS = [
  { key: 'free', Icon: Heart },
  { key: 'pdf', Icon: FileDown },
  { key: 'hebrew', Icon: Sparkles },
] as const;

const STEPS = [
  { key: 'step1', Icon: Images },
  { key: 'step2', Icon: Layers },
  { key: 'step3', Icon: BookOpenCheck },
] as const;

export function Landing({ recentAlbums = [] }: { recentAlbums?: AlbumSummary[] }) {
  const t = useTranslations('home');
  const ta = useTranslations('app');
  const locale = useLocale();
  const router = useRouter();
  const { data } = useMe();
  const [going, setGoing] = React.useState<'new' | 'albums' | null>(null);
  const Arrow = locale === 'he' ? ArrowLeft : ArrowRight;

  /**
   * "Start now" goes straight to the wizard. There is deliberately nothing to await: the guest
   * session is minted by `POST /api/albums` at the end of the wizard, so the button never sits
   * spinning on a round trip the visitor did not ask for. What it does spin for is the route change
   * itself, which can take a moment on a slow phone (UX-45) — and it stays spinning until the new
   * page has replaced this one.
   */
  const go = (to: 'new' | 'albums') => {
    setGoing(to);
    router.push(to === 'new' ? '/albums/new' : '/albums');
  };

  const signedIn = Boolean(data?.user && !data.user.isAnonymous);

  return (
    <>
      {/* ── hero ───────────────────────────────────────────────────────── */}
      {recentAlbums.length > 0 ? <RecentAlbums albums={recentAlbums} /> : (
      <section className="mx-auto grid max-w-6xl items-center gap-10 px-gutter py-12 md:grid-cols-2 md:py-20 lg:px-gutter-lg">
        <div className="flex flex-col items-start gap-5">
          <Logo size="lg" />
          <p className="text-lg text-ink-2">{ta('tagline')}</p>
          <h1 className="font-display text-3xl leading-tight tracking-display text-balance text-ink sm:text-4xl md:text-5xl">
            {t('heroTitle')}
          </h1>
          <p className="max-w-prose text-pretty text-ink-2">{t('heroLead')}</p>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" size="lg" onClick={() => go('new')} loading={going === 'new'} endIcon={<Arrow aria-hidden />}>
              {t('cta')}
            </Button>
            {data?.user ? (
              <Button variant="ghost" size="lg" onClick={() => go('albums')} loading={going === 'albums'}>
                {t('ctaAlbums')}
              </Button>
            ) : null}
          </div>
          <p className="text-sm text-ink-3">{t('ctaSub')}</p>
          {data?.user?.isAnonymous ? <p className="text-xs text-ink-3">{t('guestNote')}</p> : null}
          {signedIn && data?.user ? <p className="text-sm text-ink-2">{t('signedInAs', { name: data.user.name || data.user.email || '' })}</p> : null}
        </div>

        <HeroArt />
      </section>
      )}

      {/* ── value props ────────────────────────────────────────────────── */}
      <section className="border-y border-line bg-surface">
        <div className="mx-auto grid max-w-6xl gap-6 px-gutter py-12 md:grid-cols-3 lg:px-gutter-lg">
          {VALUE_PROPS.map(({ key, Icon }) => (
            <div key={key} className="flex flex-col gap-3">
              <span className="grid size-11 place-items-center rounded-lg bg-accent-soft text-accent">
                <Icon aria-hidden className="size-5" />
              </span>
              <h2 className="font-display text-xl text-ink">{t(`valueProps.${key}.title`)}</h2>
              <p className="text-pretty text-sm leading-relaxed text-ink-2">{t(`valueProps.${key}.body`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── how it works ───────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-gutter py-12 lg:px-gutter-lg">
        <h2 className="font-display text-2xl text-ink">{t('howTitle')}</h2>
        <ol className="mt-6 grid gap-4 md:grid-cols-3">
          {STEPS.map(({ key, Icon }, i) => (
            <li key={key}>
              <Card className="flex h-full flex-col gap-2 p-5">
                <div className="flex items-center gap-2">
                  <span className="grid size-7 place-items-center rounded-full bg-paper-2 text-xs font-semibold tabular-nums text-ink-2">
                    {i + 1}
                  </span>
                  <Icon aria-hidden className="size-4 text-accent" />
                </div>
                <h3 className="font-medium text-ink">{t(`how.${key}Title`)}</h3>
                <p className="text-sm text-ink-2">{t(`how.${key}Body`)}</p>
              </Card>
            </li>
          ))}
        </ol>
      </section>

      {/* ── sign-in (only when a provider is actually configured) ──────── */}
      {data && !signedIn && (data.features.google || (data.features.emailPassword ?? data.features.devLogin)) ? (
        <section className={cn('border-t border-line bg-paper-2')}>
          <div className="mx-auto max-w-md px-gutter py-12 lg:px-gutter-lg">
            <Card className="p-5">
              {/* The form is drawn as soon as we know which providers exist; there is nothing else
                  to wait for, and a spinner here only delays a form the visitor can already use. */}
              <SignInForm features={data.features} onSignedIn={() => router.push('/albums')} />
            </Card>
          </div>
        </section>
      ) : null}
    </>
  );
}
